import type {
  BacktestResult,
  Candle,
  ImportReport,
  IndicatorCatalogEntry,
  IndicatorSeries,
  Paginated,
  PortableDocument,
  RunDetail,
  RunSummary,
  StrategySpec,
} from "@strat/format";
import type {
  BenchmarkResponse,
  Bot,
  BotDetailResponse,
  BotHistoryResponse,
  BotListResponse,
  BotVerifyResponse,
  EventsResponse,
  SystemResponse,
  RobustnessResponse,
  StrategyFacets,
  StrategySummary,
  SweepResponse,
  UserPrefs,
  WalkForwardResponse,
} from "@strat/api";

import type { DownloadJob, IDataService, InventoryRow } from "./api.js";

/**
 * How a request reaches the other side.
 *
 * The methods below are the same work whichever transport carries them —
 * they build a path and a body. Parameterising the transport rather than the
 * service means the desktop build shares one implementation with the web build
 * instead of maintaining a second that can silently disagree with it.
 */
export type Transport = (path: string, init?: { method?: string; body?: string }) => Promise<unknown>;

type QueryValue = string | number | boolean | undefined;

function qs(params: Readonly<Record<string, QueryValue>>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== "");
  if (entries.length === 0) return "";
  return `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`;
}

/** The interface, over any transport. */
export function createDataService(transport: Transport): IDataService {
  const request = <T>(path: string, init?: { method?: string; body?: string }): Promise<T> =>
    transport(path, init) as Promise<T>;

  return {
    async getSymbols(marketType) {
      return (await request<{ symbols: string[] }>(`/api/data/symbols${qs({ marketType })}`)).symbols;
    },
    async getInventory() {
      return (await request<{ inventory: InventoryRow[] }>("/api/candles/inventory")).inventory;
    },
    async startDownload(symbol, timeframe, marketType, from, to) {
      return request("/api/data/download", {
        method: "POST",
        body: JSON.stringify({ symbol, timeframe, marketType, from, to }),
      });
    },
    async listDownloads() {
      return (await request<{ jobs: DownloadJob[] }>("/api/data/downloads")).jobs;
    },
    async cancelDownload(jobId) {
      await request(`/api/data/download/${jobId}/cancel`, { method: "POST" });
    },
    async deleteDataset(symbol, timeframe, marketType) {
      const params = new URLSearchParams({ symbol, timeframe, marketType });
      return (await request<{ deleted: number }>(`/api/data/dataset?${params.toString()}`, {
        method: "DELETE",
      }))!;
    },
    async getCandles(symbol, timeframe, marketType, from, to) {
      const params = new URLSearchParams({ symbol, timeframe, marketType });
      if (from) params.set("from", String(from));
      if (to) params.set("to", String(to));
      return (await request<{ candles: Candle[] }>(`/api/candles?${params.toString()}`)).candles;
    },
    async getIndicatorCatalog() {
      return (await request<{ catalog: IndicatorCatalogEntry[] }>("/api/indicators/catalog")).catalog;
    },
    async getIndicatorSeries(symbol, timeframe, marketType, defs, from, to) {
      return (
        await request<{ series: IndicatorSeries[] }>("/api/indicators/series", {
          method: "POST",
          body: JSON.stringify({ symbol, timeframe, marketType, defs, from, to }),
        })
      ).series;
    },
    async listStrategies(params = {}) {
      return request<Paginated<StrategySummary>>(`/api/strategies${qs({ ...params })}`);
    },
    async getStrategyFacets() {
      return request<StrategyFacets>("/api/strategies/facets");
    },
    async getStrategy(id) {
      return (await request<{ strategy: StrategySpec }>(`/api/strategies/${id}`)).strategy;
    },
    async saveStrategy(spec) {
      return (
        await request<{ strategy: StrategySpec }>("/api/strategies", {
          method: "POST",
          body: JSON.stringify(spec),
        })
      ).strategy;
    },
    async deleteStrategy(id) {
      await request(`/api/strategies/${id}`, { method: "DELETE" });
    },
    async previewBacktest(args) {
      return request<BacktestResult>("/api/backtest/preview", {
        method: "POST",
        body: JSON.stringify(args),
      });
    },
    async saveBacktest(args) {
      return request<RunDetail>("/api/backtest/save", {
        method: "POST",
        body: JSON.stringify(args),
      });
    },
    async runBenchmark(args) {
      return request<BenchmarkResponse>("/api/backtest/benchmark", {
        method: "POST",
        body: JSON.stringify(args),
      });
    },
    async runSweep(args) {
      return request<SweepResponse>("/api/backtest/sweep", {
        method: "POST",
        body: JSON.stringify(args),
      });
    },
    async runWalkForward(args) {
      return request<WalkForwardResponse>("/api/backtest/walkforward", {
        method: "POST",
        body: JSON.stringify(args),
      });
    },
    async runRobustness(args) {
      return request<RobustnessResponse>("/api/backtest/robustness", {
        method: "POST",
        body: JSON.stringify(args),
      });
    },
    async listRuns(params = {}) {
      return request<Paginated<RunSummary>>(`/api/backtest/runs${qs({ ...params })}`);
    },
    async getRun(id) {
      return request<RunDetail>(`/api/backtest/runs/${id}`);
    },
    async rerunRun(id) {
      return request<RunDetail>(`/api/backtest/runs/${id}/rerun`, { method: "POST" });
    },
    async deleteRun(id) {
      await request(`/api/backtest/runs/${id}`, { method: "DELETE" });
    },
    async exportItems(req) {
      return request<PortableDocument>("/api/portable/export", {
        method: "POST",
        body: JSON.stringify(req),
      });
    },
    async importDocument(document, options) {
      return request<ImportReport>("/api/portable/import", {
        method: "POST",
        body: JSON.stringify({ document, options }),
      });
    },
    async getPreferences() {
      return (await request<{ prefs: UserPrefs }>("/api/preferences")).prefs;
    },
    async savePreferences(prefs) {
      return (
        await request<{ prefs: UserPrefs }>("/api/preferences", {
          method: "PUT",
          body: JSON.stringify({ prefs }),
        })
      ).prefs;
    },

    async listBots() {
      return request<BotListResponse>("/api/bots");
    },
    async getBot(id) {
      return request<BotDetailResponse>(`/api/bots/${id}`);
    },
    async botHistory(id) {
      return request<BotHistoryResponse>(`/api/bots/${id}/history`);
    },
    async deployBot(req) {
      return request<Bot>("/api/bots", { method: "POST", body: JSON.stringify(req) });
    },
    async startBot(id) {
      return request<Bot>(`/api/bots/${id}/start`, { method: "POST", body: "{}" });
    },
    async stopBot(id) {
      return request<Bot>(`/api/bots/${id}/stop`, { method: "POST", body: "{}" });
    },
    async commandBot(id, command) {
      return request<{ summary: unknown }>(`/api/bots/${id}/command`, {
        method: "POST",
        body: JSON.stringify({ command }),
      });
    },
    async deleteBot(id) {
      await request(`/api/bots/${id}`, { method: "DELETE" });
    },
    async verifyBot(id) {
      return request<BotVerifyResponse>(`/api/bots/${id}/verify`, { method: "POST", body: "{}" });
    },
    async listEvents(query = {}) {
      return request<EventsResponse>(`/api/events${qs({ ...query })}`);
    },
    async getSystem() {
      return request<SystemResponse>("/api/system");
    },
  };
}
